import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconDownload } from '@tabler/icons-react';
import styles from './ReportMetrics.module.scss';
import { ProjectDto } from '../../api/model';
import { buildReportData, getProjectHealthStatus, PRESET_PHASE_TYPES } from './reportMockData';
import KpiCard from './widgets/KpiCard';
import HealthCard from './widgets/HealthCard';
import ChartCard from './widgets/ChartCard';
import TrendChart from './widgets/TrendChart';
import CategoryBarChart from './widgets/CategoryBarChart';
import StatusDonut from './widgets/StatusDonut';
import StackedStatusBar from './widgets/StackedStatusBar';

interface ReportMetricsProps {
  project?: ProjectDto;
}

const ReportMetrics: React.FC<ReportMetricsProps> = ({ project }) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const fallbackPhases = useMemo(
    () => PRESET_PHASE_TYPES.map((key) => t(`gantt:phaseTypes.${key}`)),
    [t],
  );
  const data = useMemo(
    () => buildReportData(project, fallbackPhases),
    [project, fallbackPhases],
  );

  const subtitle = project?.name
    ? t('report.subtitle', { project: project.name })
    : t('report.subtitleFallback');

  return (
    <>
      <div className={styles.planHeader}>
        <div className={styles.planHeaderContent}>
          <h2 className={styles.planTitle}>{t('report.title')}</h2>
          <p className={styles.planDescription}>{subtitle}</p>
        </div>
        <div className={styles.planHeaderActions}>
          <span className={styles.demoBadge}>{t('report.demoBadge')}</span>
          <button
            type="button"
            className={styles.exportButton}
            onClick={() => undefined}
          >
            <IconDownload size={16} stroke={1.8} />
            {t('report.export')}
          </button>
        </div>
      </div>

      <div className={styles.dashboard}>
        <HealthCard
          title={t('report.health.title')}
          score={data.health}
          statusLabel={t(`report.health.status.${getProjectHealthStatus(data.health).key}`)}
          scoreLabel={t('report.health.scoreLabel')}
        />

        <div className={styles.kpiRow}>
          {data.kpis.map((datum) => (
            <KpiCard
              key={datum.key}
              label={t(`report.kpi.${datum.key}.label`)}
              hint={t(`report.kpi.${datum.key}.hint`)}
              datum={datum}
            />
          ))}
        </div>

        <div className={styles.chartGrid}>
          <div className={styles.spanTwo}>
            <ChartCard title={t('report.trend.title')} subtitle={t('report.trend.subtitle')}>
              <TrendChart data={data.actionPointTrend} />
            </ChartCard>
          </div>
          <ChartCard title={t('report.byStatus.title')} subtitle={t('report.byStatus.subtitle')}>
            <StatusDonut data={data.taskStatus} />
          </ChartCard>
          <div className={styles.spanFull}>
            <ChartCard title={t('report.byPhase.title')} subtitle={t('report.byPhase.subtitle')}>
              <CategoryBarChart
                data={data.phaseProgress}
                unit="%"
                max={100}
                seriesName={t('report.byPhase.seriesName')}
              />
            </ChartCard>
          </div>
          <div className={styles.spanFull}>
            <ChartCard
              title={t('report.byCriticality.title')}
              subtitle={t('report.byCriticality.subtitle')}
            >
              <StackedStatusBar data={data.deliverablesByPhase} />
            </ChartCard>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportMetrics;
